/**
 * zCRM vocabulary dictionary
 * --------------------------------------------------------------------------
 * One entry per stored zInspector name, with a label for each mode.
 *   pm     = property-manager mode (zInspector's own wording)
 *   vendor = vendor CRM mode (OSP and other vendors)
 * A label of `null` means "hidden in this mode".
 *
 * Sources: "Vendor CRM on zInspector — Discovery Handoff" (Oct 7, 2026) and crm-vocabulary.md v2,
 * incl. §11 (live API values), §12 (corrections) and §13 (repurposed fields). Where they differ, §13 wins.
 * Rule: add an entry in the same change as the field or value, both modes filled.
 *
 * Use with Atomic CRM's i18n (ra-i18n-polyglot): `messagesFor("vendor")` returns
 * a nested object you merge into the English messages under `zi.*`.
 */

export type Mode = "pm" | "vendor";
export type Entry = { pm: string | null; vendor: string | null };
export type Section = Record<string, Entry>;

const same = (label: string): Entry => ({ pm: label, vendor: label });
const hiddenInVendor = (label: string): Entry => ({ pm: label, vendor: null });

/** Objects (tables and task types) */
export const objects: Section = {
  property_account: { pm: null, vendor: "Account" },
  property_dwelling: { pm: "Property / Unit", vendor: "Client property" },
  lease: { pm: "Lease", vendor: "Engagement" },
  contact_tenant: { pm: "Tenant", vendor: "Client contact" },
  contact_owner: { pm: "Owner", vendor: "Parent company" },
  contact_vendor: { pm: "Vendor", vendor: "Supplier" },
  user: { pm: "User", vendor: "Team member" },
  user_group: { pm: "Group", vendor: "Market" },
  task_sales: { pm: "Task", vendor: "Deal" },
  task_prospect: { pm: "Task", vendor: "Lead" },
  task_followup: { pm: "Follow-up", vendor: "Meeting / Call" },
  task_onboarding: { pm: "Onboarding", vendor: "Implementation" },
  task_support: { pm: "Support", vendor: "Ticket" },
  process: { pm: "Process", vendor: "Playbook" },
  todo: { pm: "To Do", vendor: "Step" },
  thread_entry: { pm: "Communication", vendor: "Timeline entry" },
  document: { pm: "Report", vendor: "Report delivered" },
  article: { pm: "Article", vendor: "Reply template" },
  zdata_account_area: { pm: "zData", vendor: "Account details" },
};

/** Lease fields. Keys are the API field names. */
export const leaseFields: Section = {
  PrimaryTenant: { pm: "Primary tenant", vendor: "Main contact" },
  Tenants: { pm: "Tenants", vendor: "Participants" },
  LeaseStart: { pm: "Lease start", vendor: "Effective date" },
  LeaseEnd: { pm: "Lease end", vendor: "Renewal date" },
  MoveIn: { pm: "Move in", vendor: "First-order target" },
  MoveOut: { pm: "Move out", vendor: "Ended on" },
  Rent: { pm: "Rent", vendor: "Pricing" },
  Status: { pm: "Status", vendor: "Engagement state" },
  Type: { pm: "Lease type", vendor: "Engagement type" },
  Comments: { pm: "Comments", vendor: "Short note" },
  AccountingID: { pm: "Accounting ID", vendor: "Legacy ID" },
  TIStatus: hiddenInVendor("Tenant inspection"),
  TMIStatus: hiddenInVendor("Tenant move-in"),
  SigStatus: { pm: "Signature status", vendor: "Agreement state" },
};

/** Lease status values. Keys are the stored values. */
export const leaseStatus: Section = {
  Listed: { pm: "Listed", vendor: "Prospect" },
  "Moving In": { pm: "Moving In", vendor: "Signed, not started" },
  Current: { pm: "Current", vendor: "Active client" },
  "Current - Renewal": { pm: "Current - Renewal", vendor: "Renewing" },
  "Moving Out": { pm: "Moving Out", vendor: "Winding down" },
  "SD Returned": { pm: "SD Returned", vendor: "Former client" },
  // Roommate and deposit statuses: hidden in vendor mode.
  "Moving In - Roommate": hiddenInVendor("Moving In - Roommate"),
  "Moving Out - Roommate": hiddenInVendor("Moving Out - Roommate"),
  "Moved Out - SD Pending": hiddenInVendor("Moved Out - SD Pending"),
  "Moved Out - SD Pending - Roommate": hiddenInVendor(
    "Moved Out - SD Pending - Roommate",
  ),
  "SD Returned - Roommate": hiddenInVendor("SD Returned - Roommate"),
};

/** Lease signature status values */
export const leaseSigStatus: Section = {
  "No Signatures": { pm: "No Signatures", vendor: "Not sent" },
  "Own Notified of Lease/Renewal": hiddenInVendor(
    "Own Notified of Lease/Renewal",
  ),
  "Lease/Renewal Sent": { pm: "Lease/Renewal Sent", vendor: "Sent" },
  "Ten Sig - Partial": { pm: "Ten Sig - Partial", vendor: "Partly signed" },
  "Ten Sig - Complete": { pm: "Ten Sig - Complete", vendor: "Client signed" },
  "Ten Comp - Sent to Owner": {
    pm: "Ten Comp - Sent to Owner",
    vendor: "Awaiting countersign",
  },
  Complete: { pm: "Complete", vendor: "Signed" },
};

/** Lease type values */
export const leaseType: Section = {
  "Not Specified": { pm: "Not Specified", vendor: "Not specified" },
  Fixed: { pm: "Fixed", vendor: "Fixed term" },
  "Month-to-Month": { pm: "Month-to-Month", vendor: "Rolling" },
};

/** Task fields. Keys are the API field names. */
export const taskFields: Section = {
  Action: { pm: "Task type", vendor: "Type" },
  Description: { pm: "Description", vendor: "Subject" },
  Notes: { pm: "Notes", vendor: "Short note" },
  DateCreated: { pm: "Created", vendor: "Created" },
  LastModified: { pm: "Modified", vendor: "Updated" },
  Property: { pm: "Property", vendor: "Account" },
  IncludeCalendar: { pm: "Add to calendar", vendor: "On my calendar" },
  ManagedBy: { pm: "Manager", vendor: "Owner" },
  AssignedToUser: { pm: "Assigned", vendor: "Assigned to" },
  AssignedToContact: { pm: "Vendor", vendor: "Client-side owner" },
  Followers: { pm: "Followers", vendor: "Watching" },
  ReferenceNumber: { pm: "Reference", vendor: "Number" },
  ExternalReferenceNumber: { pm: "External", vendor: "Legacy ID" },
  StartDate: { pm: "Appointment", vendor: "When" },
  NextToDoName: { pm: "Next To Do", vendor: "Next step" },
  TenantNames: { pm: "Tenant details", vendor: "Contact name" },
  PhoneNumber: { pm: "Phone", vendor: "Contact phone" },
  Email: { pm: "Email", vendor: "Contact email" },
  Priority: { pm: "Priority", vendor: "Priority" },
  // Repurposed fields (see crm-config.ts). Hidden by their stored name in vendor mode,
  // shown under their CRM name instead.
  EntranceInstruction: { pm: "Entrance instruction", vendor: "Deal type" },
  Key: { pm: "Key", vendor: "Product" },
  KeyDesc: { pm: "Key description", vendor: "Loss reason" },
  AuthorizedLimit: { pm: "Authorized limit", vendor: "Value" },
  WorkOrderInstructions: {
    pm: "Work-order instructions",
    vendor: "Deal notes",
  },
  Pets: { pm: "Pets", vendor: "Booked at booth" },
};

/**
 * Task status values, generic labels (tickets, follow-ups, onboarding).
 * Sales tasks do NOT use these: their status is shown as a lifecycle stage (crm-config.ts → STAGES).
 */
export const taskStatus: Section = {
  New: { pm: "New", vendor: "Open" },
  "In Progress": { pm: "In Progress", vendor: "In progress" },
  "Approval Requested": {
    pm: "Approval Requested",
    vendor: "Waiting on client",
  },
  "Approval Received": { pm: "Approval Received", vendor: "Client replied" },
  Deferred: { pm: "Deferred", vendor: "Snoozed" },
  Closed: { pm: "Closed", vendor: "Done" },
  Canceled: { pm: "Canceled", vendor: "Cancelled" },
  // Bid, work-order and invoice statuses: hidden in vendor mode outside the pipeline.
  "Bid Requested": hiddenInVendor("Bid Requested"),
  "Bid(s) Received": hiddenInVendor("Bid(s) Received"),
  "Work Order Sent": hiddenInVendor("Work Order Sent"),
  "Work In Progress": hiddenInVendor("Work In Progress"),
  "Work Complete": hiddenInVendor("Work Complete"),
  "Invoice Received": hiddenInVendor("Invoice Received"),
};

/** Priority 1–5 */
export const priority: Section = {
  "1": { pm: "1", vendor: "Urgent" },
  "2": { pm: "2", vendor: "High" },
  "3": { pm: "3", vendor: "Normal" },
  "4": { pm: "4", vendor: "Low" },
  "5": { pm: "5", vendor: "Someday" },
};

/** Process step types */
export const stepTypes: Section = {
  BOOLEAN: { pm: "To Do", vendor: "Step" },
  NOTE: same("Note"),
  EMAIL: { pm: "Email", vendor: "Send email" },
  TASK: { pm: "New Task", vendor: "Create follow-up" },
  DOCUMENT: { pm: "Document", vendor: "Request document" },
  INSPECTION_TMI: hiddenInVendor("Tenant move-in inspection"),
  INSPECTION_TI: hiddenInVendor("Tenant inspection"),
  WORK_ORDER: hiddenInVendor("Work order"),
  // TODO verify: stored names of "status change" and "zAssistant email" are not in the docs.
  STATUS_CHANGE: { pm: "Status change", vendor: "Change status" },
  ZASSISTANT_EMAIL: { pm: "zAssistant email", vendor: "AI email" },
};

/** Account (property) and contact fields */
export const propertyFields: Section = {
  Name: { pm: "Property name", vendor: "Account name" },
  Address: { pm: "Address", vendor: "Office address" },
  StreetAddress: { pm: "Street", vendor: "Street" },
  StreetAddress2: { pm: "Street 2", vendor: "Suite / floor" },
  City: same("City"),
  State: same("State"),
  Zip: same("Zip"),
  Country: same("Country"),
  UserGroup: { pm: "Group", vendor: "Market" },
  AccountingID: { pm: "Accounting ID", vendor: "Legacy ID" },
  LastActivity: { pm: "Last activity", vendor: "Last touch" },
  LastUpdate: same("Updated"),
  last_document_date: { pm: "Last report", vendor: "Last report delivered" },
  IsArchived: { pm: "Archived", vendor: "Former client" },
  PropertyPhoto: { pm: "Photo", vendor: "Logo" },
  PropertyType: hiddenInVendor("Property type"),
  Vacancy: hiddenInVendor("Vacancy"),
  IsMultiUnit: hiddenInVendor("Multi-unit"),
  IsCommon: hiddenInVendor("Common area"),
  IsMobileSync: hiddenInVendor("Sync to mobile"),
  HasPanoramics: hiddenInVendor("Has panoramics"),
  LastTenancyUpdate: hiddenInVendor("Tenancy updated"),
};

/** Contact type values */
export const contactType: Section = {
  TENANT: { pm: "Tenant", vendor: "Client contact" },
  OWNER: { pm: "Owner", vendor: "Parent company" },
  VENDOR: { pm: "Vendor", vendor: "Supplier" },
  OTHER: same("Other"),
};

export const contactFields: Section = {
  FirstName: same("First name"),
  LastName: same("Last name"),
  Type: { pm: "Contact type", vendor: "Contact type" },
  Company: { pm: "Company", vendor: "Account name" },
  Email: same("Email"),
  OfficePhone: { pm: "Land line", vendor: "Phone" },
  MobilePhone: { pm: "Phone (SMS)", vendor: "Mobile (SMS)" },
  Comments: { pm: "Comments", vendor: "Notes" },
  AccountingID: { pm: "Accounting ID", vendor: "Legacy ID" },
  Category: { pm: "Category", vendor: null },
  // Repurposed (see crm-config.ts)
  TaxId: { pm: "Tax ID", vendor: "Role" },
  Is1099Required: { pm: "1099 required", vendor: "Marketing consent" },
  IsPrimary: { pm: "Primary", vendor: "Decision maker" },
  FaxNumber: hiddenInVendor("Fax"),
};

export const vocabulary = {
  objects,
  leaseFields,
  leaseStatus,
  leaseSigStatus,
  leaseType,
  taskFields,
  taskStatus,
  priority,
  stepTypes,
  propertyFields,
  contactType,
  contactFields,
};

/** Label for one stored name; `null` = hide it. Falls back to the stored name. */
export function label(
  section: Section,
  stored: string,
  mode: Mode,
): string | null {
  const entry = section[stored];
  if (!entry) return stored;
  return entry[mode];
}

/** Nested messages for ra-i18n-polyglot, e.g. translate("zi.leaseStatus.Current"). Hidden entries are omitted. */
export function messagesFor(mode: Mode) {
  const out: Record<string, Record<string, string>> = {};
  for (const [name, section] of Object.entries(vocabulary)) {
    out[name] = {};
    for (const [stored, entry] of Object.entries(section)) {
      const value = entry[mode];
      if (value !== null) out[name][stored] = value;
    }
  }
  return { zi: out };
}
